import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Stack, Typography, Avatar, IconButton } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useHistory } from "react-router-dom";
import ApiService from "../../services/api-service";
import VideoApiService from "../../services/video-api-service";
import notifier from "../../components/Notifier";
import GalleryDetailModal from "../../pages/Gallery/GalleryDetailModal";
import ImageCarousel from "../../pages/Gallery/ImageCarousel";
import VideoDetailModal from "./VideoDetailModal";
import { SERVER_URL } from "../../constants/api";
import "./FeedCard.scss";

// Card for non-music feed items: videos and gallery images. Mirrors FeedCard's
// outer frame (.feed-card-bg/.feed-item) so the unified feed looks consistent.
// Videos autoplay (muted) while active; photos get like/comment/share actions.
const FeedMediaCard = ({ item, isActive = false, cardRef, onPlay = () => {}, ...rest }) => {
  const history = useHistory();
  const videoRef = useRef(null);

  const isVideo = item?.contentType === "video";
  const isPhoto = item?.contentType === "gallery";

  const [liked, setLiked] = useState(Boolean(item?.isLiked));
  const [likesCount, setLikesCount] = useState(item?.likesCount || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);

  useEffect(() => {
    setLiked(Boolean(item?.isLiked));
    setLikesCount(item?.likesCount || 0);
  }, [item]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isActive) {
      el.muted = true;
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      el.pause();
    }
  }, [isActive]);

  if (!item) return null;

  const uploaderId = item.uploader && (item.uploader._id || item.uploader.uId || item.uploader.id);
  const name =
    item.displayName ||
    (item.uploader &&
      (item.uploader.displayArtistName ||
        `${item.uploader.firstName || ""} ${item.uploader.lastName || ""}`.trim())) ||
    "Unknown";
  const avatar = item.profileImg || (item.uploader && item.uploader.profileImg && item.uploader.profileImg.filePath);
  const heading = isVideo ? item.title : item.caption;

  const goToProfile = (e) => {
    e.stopPropagation();
    if (uploaderId) history.push(`/app/ArtistProfile/${uploaderId}`);
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    const next = !liked;
    setLiked(next);
    setLikesCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      let res;
      if (isVideo) {
        res = next ? await VideoApiService.likeVideo(item._id) : await VideoApiService.unlikeVideo(item._id);
      } else {
        res = await ApiService.GALLERY_TOGGLE_LIKE(item._id);
      }
      const data = (res && (res.result || res.data)) || null;
      if (data && typeof data.likesCount === "number") {
        setLiked(Boolean(data.liked));
        setLikesCount(data.likesCount);
      }
    } catch (err) {
      setLiked(!next);
      setLikesCount((c) => Math.max(0, c + (next ? -1 : 1)));
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    // Videos have a real share page; photos have none yet, so fall back to the
    // uploader's in-app gallery.
    const url = isVideo
      ? `${SERVER_URL}/share/video/${item._id}`
      : `${window.location.origin}/app/Gallery/${uploaderId || ""}`;
    const shareData = { title: `${name} on Siki`, text: (isVideo ? item.title : item.caption) || "", url };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        notifier.success("Link copied to clipboard!");
      }
    } catch (err) {
      // user dismissed the share sheet — ignore
    }
  };

  return (
    <Box className="feed-card-bg">
      <Paper
        elevation={6}
        ref={cardRef}
        data-idx={rest["data-idx"]}
        onClick={onPlay}
        sx={{
          width: { xs: "96vw", sm: "min(680px, 96vw)" },
          m: "24px auto",
          display: "flex",
          flexDirection: "column",
          background: "rgba(0,0,0,0.88)",
          border: isActive ? "2px solid var(--primary-color)" : "1px solid #1c1c24",
          borderRadius: "20px",
          overflow: "hidden",
        }}
      >
        {/* Uploader header */}
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={avatar}
              sx={{ width: 36, height: 36, cursor: uploaderId ? "pointer" : "default" }}
              onClick={goToProfile}
            />
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ cursor: uploaderId ? "pointer" : "default", fontWeight: 700, color: "#fff" }}
                onClick={goToProfile}
              >
                {name}
              </Typography>
              <Typography variant="caption" sx={{ color: "#bbb" }}>
                {isVideo ? "Video" : "Photo"}
                {item.releaseDate ? ` · ${new Date(item.releaseDate).toLocaleDateString()}` : ""}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Media */}
        <Box
          sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#000" }}
        >
          {isVideo ? (
            <video
              ref={videoRef}
              src={item.filePath}
              poster={item.thumbnail || undefined}
              controls
              playsInline
              loop
              style={{ width: "100%", maxHeight: "72vh", objectFit: "contain", display: "block" }}
            />
          ) : (
            <ImageCarousel
              images={
                item.images && item.images.length
                  ? item.images
                  : [{ filePath: item.filePath || item.thumbnail }]
              }
              alt={item.caption || ""}
            />
          )}
        </Box>

        {/* Media actions: like / comment / share */}
        {(isPhoto || isVideo) && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.5 }}>
            <IconButton onClick={handleLike} sx={{ color: liked ? "#ff4d6d" : "#fff" }}>
              {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
            {likesCount > 0 && <Typography sx={{ color: "#ddd", fontSize: 14 }}>{likesCount}</Typography>}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setCommentsOpen(true);
              }}
              sx={{ color: "#fff" }}
            >
              <ChatBubbleOutlineIcon />
            </IconButton>
            {item.commentsCount > 0 && (
              <Typography sx={{ color: "#ddd", fontSize: 14 }}>{item.commentsCount}</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={handleShare} sx={{ color: "#fff" }}>
              <IosShareIcon />
            </IconButton>
          </Stack>
        )}

        {/* Caption / title */}
        {heading ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ color: "#eee", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {heading}
            </Typography>
          </Box>
        ) : null}
      </Paper>

      {/* Comments + full-size view (reuses the per-type detail modal) */}
      {isPhoto && commentsOpen && (
        <GalleryDetailModal
          item={{ ...item, user: item.uploader, isLiked: liked, likesCount }}
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          onItemUpdate={(_id, patch) => {
            if (typeof patch.likesCount === "number") setLikesCount(patch.likesCount);
            if (typeof patch.isLiked === "boolean") setLiked(patch.isLiked);
          }}
        />
      )}
      {isVideo && commentsOpen && (
        <VideoDetailModal
          item={{ ...item, isLiked: liked, likesCount }}
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          onItemUpdate={(_id, patch) => {
            if (typeof patch.likesCount === "number") setLikesCount(patch.likesCount);
            if (typeof patch.isLiked === "boolean") setLiked(patch.isLiked);
          }}
        />
      )}
    </Box>
  );
};

export default FeedMediaCard;
